import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor, releaseIdempotencyKey, type ApiClientError } from '@/lib/http-core';
import { errorFromResponse } from '@/lib/errors';

/*
 * 주문 현금영수증(order V76, order PR #24·payment PR #2) — 무통장입금 주문의 신청·발급 이력 조회와 관리자 발급·취소.
 * 입금확인 시 자동 발급, 취소·환불 시 자동 취소되지만 운영자가 상태를 보고 실패 건을 재발급하거나 수동 취소한다.
 *
 * BE `/api/admin/v1/orders/{orderId}/cash-receipt` — BO 는 게이트웨이 `/admin/v1/orders/**` 경유(다른 주문 액션과 같음).
 *   GET              조회(신청 + 발급 이력: 쇼핑몰 발급 API · Toss 결제창 PG_WINDOW · 고도몰 이관 LEGACY)
 *   POST             발급 — body 가 있으면 신청을 그 값으로 바꾸고 발급, 없으면 저장된 신청으로 재발급
 *                    (살아 있는 발급분이 있으면 409 = 먼저 취소, 입금확인 전이면 409 지만 신청은 저장 → 입금확인 시 자동 발급)
 *   POST /cancel     취소 — { amount?, reason? }, amount 생략 = 잔액 전부(미확정·Toss 거절은 409 + 사유)
 * 조회 OPS_ADMIN·CS_AGENT·FULFILLMENT_AGENT·PICKUP_AGENT·SUPER_ADMIN·ORDER_VIEW / 발급·취소 OPS_ADMIN·CS_AGENT·SUPER_ADMIN.
 */

export type CashReceiptIssueStatus = 'WAITING_DEPOSIT' | 'REQUESTED' | 'IN_PROGRESS' | 'ISSUED' | 'FAILED' | 'CANCELED';
export type CashReceiptType = 'INCOME_DEDUCTION' | 'EXPENSE_PROOF';
export type CashReceiptIdentityType = 'PHONE' | 'BUSINESS_NO';

export interface CashReceiptRequestView {
  receiptType: CashReceiptType | string;
  identityType: CashReceiptIdentityType | string;
  identityMasked: string | null;
  /** CHECKOUT(주문서) · MYPAGE(마이페이지) · ADMIN(관리자) */
  source: string | null;
  issueStatus: CashReceiptIssueStatus | string;
  lastFailureMessage: string | null;
  requestedAt: string | null;
}

export interface CashReceiptItem {
  cashReceiptId: string;
  /** API(쇼핑몰 발급) · PG_WINDOW(Toss 결제창) · LEGACY(고도몰 이관 — 조회 전용) */
  source: string;
  status: string;
  receiptType: string | null;
  identityMasked: string | null;
  amount: number;
  taxFreeAmount: number;
  canceledAmount: number;
  remainingAmount: number;
  issueNumber: string | null;
  receiptUrl: string | null;
  failureMessage: string | null;
  requestedAt: string | null;
  issuedAt: string | null;
  canceledAt: string | null;
  legacyApprovalNo: string | null;
}

export interface OrderCashReceipt {
  orderId: string;
  /** 현금영수증 대상 주문(무통장입금 등) 여부 — 신청·발급 정보 변경을 열지 판단. */
  eligible: boolean;
  request: CashReceiptRequestView | null;
  receipts: CashReceiptItem[];
  /** false 면 결제 서비스 조회 실패 — receipts 가 비어도 "이력 없음"이 아니다. */
  receiptsAvailable: boolean;
}

export interface CashReceiptIssueInput {
  receiptType: CashReceiptType;
  identityType: CashReceiptIdentityType;
  identityNumber: string;
}

export const CASH_RECEIPT_WRITE_ROLES = ['OPS_ADMIN', 'CS_AGENT', 'SUPER_ADMIN'] as const;

const path = (orderId: string) => `/admin/v1/orders/${encodeURIComponent(orderId)}/cash-receipt`;
export const orderCashReceiptQueryKey = (orderId: string) => ['admin', 'order', orderId, 'cash-receipt'] as const;

type Rec = Record<string, unknown>;
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const num = (v: unknown): number => (typeof v === 'number' ? v : 0);

const normalize = (raw: Rec): OrderCashReceipt => {
  const req = raw.request as Rec | null | undefined;
  return {
    orderId: String(raw.orderId ?? ''),
    eligible: raw.eligible === true,
    request: req
      ? {
          receiptType: String(req.receiptType ?? ''),
          identityType: String(req.identityType ?? ''),
          identityMasked: str(req.identityMasked),
          source: str(req.source),
          issueStatus: String(req.issueStatus ?? ''),
          lastFailureMessage: str(req.lastFailureMessage),
          requestedAt: str(req.requestedAt),
        }
      : null,
    receipts: (Array.isArray(raw.receipts) ? (raw.receipts as Rec[]) : []).map((r) => ({
      cashReceiptId: String(r.cashReceiptId ?? ''),
      source: String(r.source ?? ''),
      status: String(r.status ?? ''),
      receiptType: str(r.receiptType),
      identityMasked: str(r.identityMasked),
      amount: num(r.amount),
      taxFreeAmount: num(r.taxFreeAmount),
      canceledAmount: num(r.canceledAmount),
      remainingAmount: num(r.remainingAmount),
      issueNumber: str(r.issueNumber),
      receiptUrl: str(r.receiptUrl),
      failureMessage: str(r.failureMessage),
      requestedAt: str(r.requestedAt),
      issuedAt: str(r.issuedAt),
      canceledAt: str(r.canceledAt),
      legacyApprovalNo: str(r.legacyApprovalNo),
    })),
    receiptsAvailable: raw.receiptsAvailable !== false,
  };
};

const readData = async (res: Response): Promise<OrderCashReceipt> => {
  if (!res.ok) throw await errorFromResponse(res);
  const env = (await res.json()) as { data?: Rec | null };
  if (!env.data) throw new Error('no data');
  return normalize(env.data);
};

export const useOrderCashReceiptQuery = (orderId: string) =>
  useQuery<OrderCashReceipt, ApiClientError>({
    queryKey: orderCashReceiptQueryKey(orderId),
    queryFn: async () => readData(await adminFetch(path(orderId))),
    // 404(BE 배포 전)·403(조회 권한 없음)은 다시 시도해도 같다.
    retry: false,
  });

const post = async (url: string, body: unknown): Promise<OrderCashReceipt> => {
  const res = await adminFetch(url, {
    method: 'POST',
    headers: {
      ...(body === null ? {} : { 'Content-Type': 'application/json' }),
      'Idempotency-Key': idempotencyKeyFor('POST', url, body),
    },
    ...(body === null ? {} : { body: JSON.stringify(body) }),
  });
  const data = await readData(res);
  // 재발급→취소→재발급처럼 같은 본문을 다시 보낼 때 첫 응답 재생으로 묻히지 않게 키를 푼다.
  releaseIdempotencyKey('POST', url, body);
  return data;
};

export const useOrderCashReceiptMutations = (orderId: string) => {
  const qc = useQueryClient();
  const onSuccess = (data: OrderCashReceipt) => qc.setQueryData(orderCashReceiptQueryKey(orderId), data);
  // 409(입금확인 전 — 신청은 저장됨 등)도 서버 상태가 바뀌었을 수 있어 다시 읽는다.
  const onSettled = () => qc.invalidateQueries({ queryKey: orderCashReceiptQueryKey(orderId) });

  /** input 이 없으면 저장된 신청으로 재발급. */
  const issue = useMutation<OrderCashReceipt, ApiClientError, CashReceiptIssueInput | null>({
    mutationFn: (input) => post(path(orderId), input),
    onSuccess,
    onSettled,
  });
  const cancel = useMutation<OrderCashReceipt, ApiClientError, { amount?: number; reason?: string }>({
    mutationFn: (body) => post(`${path(orderId)}/cancel`, body),
    onSuccess,
    onSettled,
  });
  return { issue, cancel };
};
